/**
 * "Why this matters" cards.
 *
 * Numbered, so the eye has an order to follow on a long phone screen, with a
 * hairline keyline in the service's own accent colour down the left of each
 * card. `centreLastRow` handles an odd count (five on the financial statements
 * page) by letting the final row centre itself instead of leaving a gap.
 */
export const BenefitGrid = ({
  heading,
  lead,
  note,
  items,
  slug,
  centreLastRow = false,
  tinted = false,
  anchorId,
}) => (
  <section
    /* Set only where the section is used as a sitelink destination; the id
       brings its own clearance from the sticky header. */
    id={anchorId}
    data-testid={`${slug}-benefits`}
    className={[
      anchorId ? "scroll-mt-24" : "",
      tinted
        ? "border-y border-border bg-[color:var(--tt-surface-2)] py-14 sm:py-20"
        : "py-14 sm:py-20",
    ]
      .filter(Boolean)
      .join(" ")}
  >
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
      <h2 className="max-w-[34ch] text-[27px] font-bold leading-[1.15] tracking-[-0.015em] text-foreground sm:text-[34px]">
        {heading}
      </h2>
      {lead ? (
        <p className="mt-4 max-w-[62ch] text-[15px] leading-relaxed text-muted-foreground sm:text-base">
          {lead}
        </p>
      ) : null}

      {note ? (
        <p
          data-testid={`${slug}-benefits-note`}
          className="mt-5 max-w-[64ch] border-l-[3px] border-l-[color:var(--tt-accent)] bg-white py-3 pl-4 text-[14px] leading-relaxed text-foreground"
        >
          {note}
        </p>
      ) : null}

      <div
        className={
          centreLastRow
            ? "mt-9 flex flex-wrap justify-center gap-4"
            : "mt-9 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
        }
      >
        {items.map(({ icon: Icon, title, text }, index) => (
          <article
            key={title}
            data-testid={`${slug}-benefit-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`tt-card relative overflow-hidden p-6 ${
              centreLastRow ? "w-full sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.75rem)]" : ""
            }`}
          >
            <span
              aria-hidden="true"
              className="absolute inset-y-0 left-0 w-[3px] bg-[color:var(--tt-accent)]/35"
            />
            <div className="flex items-center justify-between gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[color:var(--tt-accent)]/10 text-[color:var(--tt-accent)]">
                <Icon className="h-5 w-5" aria-hidden="true" />
              </span>
              <span className="font-amount text-[13px] font-semibold text-muted-foreground/70">
                {String(index + 1).padStart(2, "0")}
              </span>
            </div>
            <h3 className="mt-5 text-[13px] font-bold uppercase leading-snug tracking-[0.08em] text-foreground">
              {title}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{text}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);
